// What the equipment dialogs offer. Types, rooms and departments hidden in Configurare are not offered
// for anything new, but an edit form keeps showing the item's current value.
import { PERIPHERAL_EQUIPMENT_TYPE_ID } from "@/config/periferice";

export function offeredTypes(types, currentId = null) {
  return (types || []).filter((t) => !t.is_hidden || t.id === currentId);
}

// rows of GET /locations (or /locations/:block, /locations/:block/:floor)
export function offeredLocations(rows, current = null) {
  const isCurrent = (l) =>
    !!current && l.block === current.block && l.floor === current.floor && l.room === current.room;
  return (rows || []).filter((l) => !l.is_hidden || isCurrent(l));
}

/**
 * Ids of the departments (rows of GET /departments) a person is a member of (their GET /users/:id `departments`):
 * by id; by name only for the old main department that is just a name (no id), since names repeat in the tree.
 * Same rule as the server (UserDepartmentService.isMemberOf).
 */
export function memberDepartmentIds(departments, memberships) {
  const ids = new Set((memberships || []).filter((m) => m.id != null).map((m) => Number(m.id)));
  const names = new Set((memberships || []).filter((m) => m.id == null).map((m) => m.name));
  return new Set((departments || []).filter((d) => ids.has(Number(d.id)) || names.has(d.name)).map((d) => Number(d.id)));
}

/**
 * Soft hint for the department picked for an item (any department may be chosen; staff put items under
 * sub-units): '' when the responsible person is a member there. Same text as the server (import preview,
 * UserDepartmentService.notMemberHint): the item then counts under the person's main department.
 */
export function notMemberHint(departments, memberships, departmentId) {
  if (!departmentId || !Array.isArray(memberships)) return "";
  if (memberDepartmentIds(departments, memberships).has(Number(departmentId))) return "";
  const main = memberships.find((m) => m.isPrimary);
  return `Persoana nu este membră a acestui departament — echipamentul va apărea la departamentul principal al persoanei${main ? ` („${main.name}”)` : ""}`;
}

/**
 * Department tree for v-treeview. A hidden department is kept only as a path to visible sub-departments
 * (disabled, not pickable) or when it is the item's current department.
 */
export function departmentTree(departments, keepId = null) {
  const byParent = new Map();
  (departments || []).forEach((d) => {
    if (!byParent.has(d.parent_id)) byParent.set(d.parent_id, []);
    byParent.get(d.parent_id).push(d);
  });
  const build = (parentId) =>
    (byParent.get(parentId) || [])
      .map((d) => {
        const children = build(d.id);
        const pickable = !d.is_hidden || d.id === keepId;
        if (!pickable && !children.length) return null;
        return { id: d.id, name: d.name, hidden: !!d.is_hidden, disabled: !pickable, children };
      })
      .filter(Boolean);
  return build(0);
}

// nr. inventar is optional only for Periferice (cables, flash drives often have none)
export function registrationOptional(typeId) {
  return typeId === PERIPHERAL_EQUIPMENT_TYPE_ID;
}

const TYPE_ICONS = {
  Calculatoare: "mdi-desktop-tower-monitor",
  Imprimanta: "mdi-printer-outline",
  MFU: "mdi-scanner",
  Copiator: "mdi-content-copy",
  Periferice: "mdi-usb-flash-drive-outline",
};
// types added in Configurare get an icon from a word in their name, else a generic one
const KEYWORD_ICONS = [
  [/laptop|notebook/, "mdi-laptop"],
  [/proiector|projector/, "mdi-projector"],
  [/monitor|ecran|display/, "mdi-monitor"],
  [/tablă|tabla|interactiv/, "mdi-presentation"],
  [/scaner|scanner/, "mdi-scanner"],
  [/imprimant|printer/, "mdi-printer-outline"],
  [/telefon|phone/, "mdi-phone-classic"],
  [/router|switch|rețea|retea|wi-?fi/, "mdi-router-wireless"],
  [/server/, "mdi-server"],
  [/ups|sursă|sursa/, "mdi-battery-charging-outline"],
  [/camer|video/, "mdi-cctv"],
  [/audio|boxe|difuzor|microfon|căști|casti/, "mdi-speaker"],
  [/tabletă|tableta|tablet/, "mdi-tablet"],
];
export function typeIcon(type) {
  if (TYPE_ICONS[type]) return TYPE_ICONS[type];
  const name = String(type || "").toLowerCase();
  const hit = KEYWORD_ICONS.find(([re]) => re.test(name));
  return hit ? hit[1] : "mdi-devices";
}
